import { useEffect, useState } from "react";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const BASE_URL = "https://us-central1-workexpress-1ffff.cloudfunctions.net/app";

const AreaNamesManagement = () => {
    const [areaNames, setAreaNames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [newAreaName, setNewAreaName] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const { user } = useAuth();

    const fetchAreaNames = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/area-names`);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("API Error Response:", errorText);
                throw new Error(`Failed to fetch area names: ${response.status} ${response.statusText}`);
            }
            const data = await response.json();
            // Handle both array and object responses
            let areaNamesArray = [];
            if (Array.isArray(data)) {
                areaNamesArray = data;
            } else if (data && Array.isArray(data.data)) {
                areaNamesArray = data.data;
            } else if (data && typeof data === 'object') {
                // If it's a single object, wrap it in an array
                areaNamesArray = [data];
            }
            // Sort by name alphabetically
            const sorted = areaNamesArray
                .filter(item => item && item.name) // Filter out invalid items
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setAreaNames(sorted);
            setError(null);
        } catch (error) {
            console.error("Error fetching area names:", error);
            setError(error.message);
            setAreaNames([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAreaNames();
        const intervalId = setInterval(fetchAreaNames, 10000); // Refresh every 10 seconds
        return () => clearInterval(intervalId);
    }, []);

    const handleAdd = async () => {
        if (!newAreaName.trim()) {
            alert("Please enter an area name");
            return;
        }

        // Check for duplicates
        if (areaNames.some(area => area.name.toLowerCase() === newAreaName.trim().toLowerCase())) {
            alert("This area name already exists");
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/area-names`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: newAreaName.trim() }),
            });

            if (!response.ok) {
                throw new Error("Failed to add area name");
            }

            setNewAreaName("");
            setIsAdding(false);
            fetchAreaNames();
            alert("Area name added successfully");
        } catch (error) {
            console.error("Error adding area name:", error);
            alert("Failed to add area name");
        }
    };

    const handleEditStart = (area) => {
        setEditingId(area.id);
        setEditValue(area.name);
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditValue("");
    };

    const handleEditSave = async (id) => {
        if (!editValue.trim()) {
            alert("Please enter an area name");
            return;
        }

        // Check for duplicates (excluding current item)
        if (areaNames.some(area => area.id !== id && area.name.toLowerCase() === editValue.trim().toLowerCase())) {
            alert("This area name already exists");
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/area-names/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: editValue.trim() }),
            });

            if (!response.ok) {
                throw new Error("Failed to update area name");
            }

            setEditingId(null);
            setEditValue("");
            fetchAreaNames();
            alert("Area name updated successfully");
        } catch (error) {
            console.error("Error updating area name:", error);
            alert("Failed to update area name");
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/area-names/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Failed to delete area name");
            }

            fetchAreaNames();
            alert("Area name deleted successfully");
        } catch (error) {
            console.error("Error deleting area name:", error);
            alert("Failed to delete area name");
        }
    };

    const handleInitialize = async () => {
        if (!window.confirm("This will add all default area names to the collection. Continue?")) {
            return;
        }

        const defaultAreas = [
            'Dwaraka Nagar',
            'MVP Colony',
            'Seethammadhara',
            'Akkayyapalem',
            'Siripuram',
            'Maddilapalem',
            'Lawson\'s Bay Colony',
            'Beach Road',
            'Gajuwaka',
            'NAD Junction',
            'Pendurthi',
            'Marripalem',
            'Kancharapalem',
            'Resapuvanipalem',
            'Muralinagar',
            'Pedda Waltair',
            'Ram Nagar',
            'Jagadamba Junction',
            'Railway New Colony',
            'Kurmannapalem',
            'Akkireddypalem',
            'Sheela Nagar',
            'Scindia',
            'Autonagar',
            'Ukkunagaram (Vizag Steel Plant Township)',
            'Aganampudi',
            'Simhachalam',
            'Gopalapatnam',
            'Sujatha Nagar',
            'Yendada',
            'Madhurawada',
            'Rushikonda',
            'Venkojipalem',
            'Isukathota',
            'Balayya Sastri Layout',
            'Lawson\'s Bay',
            'Arilova',
            'Kommadi',
            'Pothinamallayya Palem (PM Palem)',
            'Anandapuram',
        ];

        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/area-names/initialize`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ areaNames: defaultAreas }),
            });

            if (!response.ok) {
                throw new Error("Failed to initialize area names");
            }

            fetchAreaNames();
            alert("Area names initialized successfully");
        } catch (error) {
            console.error("Error initializing area names:", error);
            alert("Failed to initialize area names");
        } finally {
            setLoading(false);
        }
    };

    if (loading && areaNames.length === 0) {
        return (
            <div className="loading-container">
                <span className="loader"></span>
                <style>{`
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: #666;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
            </div>
        );
    }

    return (
        <div className="area-names-panel">
            <style>{`
        .area-names-panel {
          padding: 30px;
          margin: 0 auto;
          background: var(--bg-primary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          flex-wrap: wrap;
          gap: 15px;
        }
        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 24px;
          margin: 0;
        }
        .header-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .btn {
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
        }
        .btn-primary:hover {
          background: #2563eb;
        }
        .btn-secondary {
          background: #6b7280;
          color: white;
        }
        .btn-secondary:hover {
          background: #4b5563;
        }
        .btn-success {
          background: #10b981;
          color: white;
        }
        .btn-success:hover {
          background: #059669;
        }
        .btn-danger {
          background: #ef4444;
          color: white;
        }
        .btn-danger:hover {
          background: #dc2626;
        }
        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
        }
        .add-section {
          background: var(--bg-card, #ffffff);
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
        }
        .add-section.hidden {
          display: none;
        }
        .input-group {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .input-group input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
        }
        .input-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .table-container {
          background: var(--bg-card, #ffffff);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
          overflow: hidden;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card, #ffffff);
        }
        th {
          background: #374151;
          color: #ffffff;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid var(--border-color, #4b5563);
        }
        [data-theme="dark"] th {
          background: #1f2937;
          color: #ffffff;
          border-bottom-color: var(--border-color, #404040);
        }
        td {
          padding: 12px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          font-size: 14px;
          color: var(--text-primary, #4a5568);
          background: var(--bg-card, #ffffff);
        }
        tr {
          background: var(--bg-card, #ffffff);
        }
        [data-theme="dark"] tr {
          background: var(--bg-secondary, #2d2d2d);
        }
        tr:hover {
          background: var(--bg-hover, #f9fafb);
        }
        [data-theme="dark"] tr:hover {
          background: var(--bg-hover, #353535);
        }
        .edit-input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid #3b82f6;
          border-radius: 4px;
          font-size: 14px;
        }
        .edit-input:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .actions-cell {
          display: flex;
          gap: 8px;
        }
        .error-container {
          padding: 20px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          margin-bottom: 20px;
        }
        .empty-state {
          padding: 40px;
          text-align: center;
          color: var(--text-muted, #6b7280);
        }
        .empty-state h3 {
          color: var(--text-primary, #1f2937);
          margin-bottom: 10px;
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            align-items: flex-start;
          }
          .header-actions {
            width: 100%;
          }
          .btn {
            flex: 1;
          }
          .input-group {
            flex-direction: column;
          }
          .input-group input {
            width: 100%;
          }
        }
      `}</style>

            <div className="header">
                <h2>Area Names Management</h2>
                <div className="header-actions">
                    {areaNames.length === 0 && (
                        <button className="btn btn-secondary" onClick={handleInitialize}>
                            Initialize Default Areas
                        </button>
                    )}
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            setIsAdding(!isAdding);
                            setNewAreaName("");
                        }}
                    >
                        <FaPlus /> {isAdding ? "Cancel" : "Add Area Name"}
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-container">
                    <p>Error: {error}</p>
                </div>
            )}

            <div className={`add-section ${!isAdding ? "hidden" : ""}`}>
                <div className="input-group">
                    <input
                        type="text"
                        placeholder="Enter area name (e.g., Dwaraka Nagar)"
                        value={newAreaName}
                        onChange={(e) => setNewAreaName(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                handleAdd();
                            }
                        }}
                    />
                    <button className="btn btn-success" onClick={handleAdd}>
                        <FaSave /> Save
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            setIsAdding(false);
                            setNewAreaName("");
                        }}
                    >
                        <FaTimes /> Cancel
                    </button>
                </div>
            </div>

            <div className="table-container">
                {areaNames.length === 0 ? (
                    <div className="empty-state">
                        <h3>No Area Names Found</h3>
                        <p>Click "Initialize Default Areas" to add the default area names, or add them manually.</p>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: "50px" }}>S.No</th>
                                <th>Area Name</th>
                                <th style={{ width: "150px" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {areaNames.map((area, index) => (
                                <tr key={area.id}>
                                    <td>{index + 1}</td>
                                    <td>
                                        {editingId === area.id ? (
                                            <input
                                                type="text"
                                                className="edit-input"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleEditSave(area.id);
                                                    } else if (e.key === "Escape") {
                                                        handleEditCancel();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            area.name
                                        )}
                                    </td>
                                    <td className="actions-cell">
                                        {editingId === area.id ? (
                                            <>
                                                <button
                                                    className="btn btn-success btn-small"
                                                    onClick={() => handleEditSave(area.id)}
                                                    title="Save"
                                                >
                                                    <FaSave />
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-small"
                                                    onClick={handleEditCancel}
                                                    title="Cancel"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-primary btn-small"
                                                    onClick={() => handleEditStart(area)}
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-small"
                                                    onClick={() => handleDelete(area.id, area.name)}
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AreaNamesManagement;

